import React, { useMemo } from 'react';

import { useAnalyticsEvents, type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SmartCardProvider } from '@atlaskit/link-provider';

import { isPageSSRd, useStoreOptions } from '@confluence/content-smartlinks';
import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes';
import { useIsOnRoute } from '@confluence/route-manager/entry-points/RouteState';
import { useSessionData } from '@confluence/session-data';

import { ConfluenceSmartCardClient } from './ConfluenceSmartCardClient';
import { liveDocSmartCardResolver } from './resolvers/liveDocSmartCardResolver';

// Only log first error to console, to prevent spamming console with resolver errors
let hasLoggedResolverError = false;
const defaultConfluenceResolveHandler = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	data?: unknown,
) => {
	try {
		// NOTE FOR DEVS OF RESOLVERS: The `data` object comes from linking platform's content resolver service which is an UNSTABLE API SCHEMA.
		// Ensure your resolver logic handles invalid schemas gracefully and is able to notify your team if the schema changes and your code needs to be updated.
		liveDocSmartCardResolver(createAnalyticsEvent, data);
	} catch (resolverError) {
		// prevent errors from causing smart card provider issues
		if (!hasLoggedResolverError) {
			const logger = getLogger('ConfluenceSmartCardProvider');
			logger.error`resolver error: ${resolverError}`;
			hasLoggedResolverError = true;
		}
	}
};

const CurrentSmartCardProvider: typeof SmartCardProvider = (props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const smartCardClient = useMemo(
		() =>
			props?.client ||
			(fg('confluence_frontend_smart_card_client')
				? new ConfluenceSmartCardClient({
						onResolve: (data) => defaultConfluenceResolveHandler(createAnalyticsEvent, data),
					})
				: undefined),
		[props?.client, createAnalyticsEvent],
	);

	return <SmartCardProvider product="CONFLUENCE" client={smartCardClient} {...props} />;
};

const ConsolidatedSmartCardProvider: typeof SmartCardProvider = (props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const smartCardClient = useMemo(
		() =>
			props?.client ||
			(fg('confluence_frontend_smart_card_client')
				? new ConfluenceSmartCardClient({
						onResolve: (data) => defaultConfluenceResolveHandler(createAnalyticsEvent, data),
					})
				: undefined),
		[props?.client, createAnalyticsEvent],
	);

	const { isAdminHubAIEnabled } = useSessionData();

	const isPageSSR = isPageSSRd();
	const initialState = window['__SSR_SMARTLINKS__'];
	const storeOptions = useStoreOptions(initialState, isPageSSR);
	const presetInitialState = fg('platform_ssr_smartlinks_editor') ? storeOptions : {};

	const shouldControlDataExport = useIsOnRoute(PDF_EXPORT_PAGE);

	return (
		<SmartCardProvider
			product="CONFLUENCE"
			client={smartCardClient}
			isAdminHubAIEnabled={isAdminHubAIEnabled}
			shouldControlDataExport={shouldControlDataExport}
			{...presetInitialState}
			{...props}
		/>
	);
};

export const ConfluenceSmartCardProvider: typeof SmartCardProvider = (props) => {
	if (fg('confluence_smart_card_provider_preview_panel')) {
		return <ConsolidatedSmartCardProvider {...props} />;
	} else {
		return <CurrentSmartCardProvider {...props} />;
	}
};
